import moment from 'moment';
import { readDate } from '../../utils/appDate';

/**
 * Everything the dashboard shows, worked out from orders.
 *
 * Pure functions on purpose: the screen fetches, these decide, and both can be
 * reasoned about — and tested — without a browser.
 *
 * Two facts about the data shape the whole file:
 *
 * 1. `GET /orders/get-business-orders` already excludes canceled orders
 *    (`orderStatus: { $ne: 'canceled' }` in the controller), so nothing here
 *    has to filter them out and nothing here can count them. Cancellations
 *    come from `/orders/get-canceled-orders`, which is manager-only.
 *
 * 2. `order.date` is written by whichever client placed the order. The till
 *    sends a local wall-clock string that the UTC server reads as UTC — three
 *    hours off — and the phone sends midnight with no time at all. `created_at`
 *    is stamped by the server on insert and is the only timestamp that can be
 *    trusted, so every clock reading and every day bucket below uses it, with
 *    `date` only as a fallback for a row that somehow has none.
 */

/** Statuses that mean the money has not been taken yet. */
export const OPEN_STATUSES = ['pending', 'preparing', 'ready', 'served'];

/**
 * When it actually happened, as opposed to when the till says it did.
 *
 * `created_at` is stamped by Mongo, so it is a REAL instant and is read with
 * plain `moment` — this is the one kind of date in the app that is not the
 * naive wall-clock text described in utils/appDate. The `date` fallback is
 * that text, so it takes `readDate` instead; reading it as a real instant put
 * an order three hours into the wrong bucket near midnight.
 */
export function orderMoment(order) {
  if (order?.created_at) return moment(order.created_at);
  return readDate(order?.date);
}

export function isOpen(order) {
  return OPEN_STATUSES.includes(String(order?.orderStatus || '').toLowerCase());
}

export function isInvoice(order) {
  return String(order?.paymentType || '').toLowerCase() === 'invoice';
}

const num = (value) => Number(value || 0);
const round2 = (value) => Math.round(value * 100) / 100;

/** Ids come back populated on some routes and bare on others. */
export function idOf(value) {
  if (!value) return '';
  return typeof value === 'object' ? value._id || '' : value;
}

/* ── the headline ───────────────────────────────────────────────────────── */

export function summarise(orders) {
  let revenue = 0;
  let gross = 0;
  let discount = 0;
  let discounted = 0;
  let openCount = 0;
  let openValue = 0;
  let invoiceCount = 0;
  let invoiceValue = 0;

  orders.forEach((order) => {
    revenue += num(order.total);
    gross += num(order.grossTotal) || num(order.total);
    if (num(order.discount) > 0) {
      discount += num(order.discount);
      discounted += 1;
    }
    if (isOpen(order)) {
      openCount += 1;
      openValue += num(order.total);
    }
    if (isInvoice(order)) {
      invoiceCount += 1;
      invoiceValue += num(order.total);
    }
  });

  return {
    revenue: round2(revenue),
    orders: orders.length,
    // An average of nothing is nothing, not NaN.
    average: orders.length ? round2(revenue / orders.length) : 0,
    gross: round2(gross),
    discount: round2(discount),
    discountedOrders: discounted,
    discountShare: gross > 0 ? round2((discount / gross) * 100) : 0,
    openCount,
    openValue: round2(openValue),
    invoiceCount,
    invoiceValue: round2(invoiceValue),
  };
}

/** The change from one period to the one it is being compared with. */
export function delta(current, previous) {
  const from = num(previous);
  const to = num(current);
  if (!from) return { amount: round2(to), percent: null, direction: to > 0 ? 'up' : 'flat' };
  const amount = round2(to - from);
  return {
    amount,
    percent: round2(((to - from) / from) * 100),
    direction: amount > 0 ? 'up' : amount < 0 ? 'down' : 'flat',
  };
}

/* ── the trend ──────────────────────────────────────────────────────────── */

/**
 * One bucket per day between two dates, days with no orders included.
 *
 * A missing day has to be a zero-height bar rather than a gap: a chart that
 * silently skips Sunday makes a closed day look like a busy one.
 */
export function dailyTotals(orders, startDate, endDate) {
  const buckets = new Map();
  const cursor = moment(startDate).startOf('day');
  const last = moment(endDate).startOf('day');

  while (cursor.isSameOrBefore(last)) {
    const key = cursor.format('YYYY-MM-DD');
    buckets.set(key, { date: key, total: 0, orders: 0 });
    cursor.add(1, 'day');
  }

  orders.forEach((order) => {
    const key = orderMoment(order).format('YYYY-MM-DD');
    const bucket = buckets.get(key);
    if (!bucket) return;
    bucket.total = round2(bucket.total + num(order.total));
    bucket.orders += 1;
  });

  return Array.from(buckets.values());
}

/** Average takings per weekday, over whatever days are in the buckets. */
export function weekdayAverages(days) {
  const sums = Array.from({ length: 7 }, () => ({ total: 0, count: 0 }));

  days.forEach((day) => {
    // Monday first: moment's Sunday is 0, and a restaurant week does not
    // start on Sunday.
    const index = (moment(day.date).day() + 6) % 7;
    sums[index].total += day.total;
    sums[index].count += 1;
  });

  const labels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return sums.map((entry, index) => ({
    label: labels[index],
    average: entry.count ? round2(entry.total / entry.count) : 0,
    days: entry.count,
  }));
}

/* ── what sold ──────────────────────────────────────────────────────────── */

/**
 * Every line of every order, added up per menu item.
 *
 * Keyed by the menu item's id where there is one, because two items can share
 * a name; the name on the line is kept because it is the name the customer was
 * charged under, even if the item has been renamed since.
 */
export function itemTotals(orders) {
  const items = new Map();

  orders.forEach((order) => {
    (order.items || []).forEach((line) => {
      const id = idOf(line.menuItem) || `name:${line.name}`;
      const entry = items.get(id) || { id, menuItemId: idOf(line.menuItem), name: line.name, quantity: 0, revenue: 0 };
      entry.quantity += num(line.quantity);
      entry.revenue = round2(entry.revenue + (num(line.subtotal) || num(line.quantity) * num(line.price)));
      items.set(id, entry);
    });
  });

  return Array.from(items.values());
}

export function topItems(orders, { by = 'quantity', limit = 7 } = {}) {
  return itemTotals(orders)
    .sort((a, b) => b[by] - a[by] || b.revenue - a.revenue)
    .slice(0, limit);
}

/**
 * Items on the menu that barely moved.
 *
 * The order line carries no category and no link back to a live menu item
 * beyond its id, so this is the menu minus what sold — which also means an
 * item added yesterday looks as idle as one nobody has ordered in a year. The
 * screen says the period out loud for that reason.
 */
export function notMoving(orders, products, { limit = 6 } = {}) {
  const sold = new Map();
  itemTotals(orders).forEach((entry) => {
    if (entry.menuItemId) sold.set(entry.menuItemId, entry.quantity);
  });

  const live = products.filter((product) => product && product.status !== 'unavailable');

  return {
    total: live.length,
    soldCount: live.filter((product) => sold.has(product._id)).length,
    rows: live
      .map((product) => ({
        _id: product._id,
        name: product.name,
        quantity: sold.get(product._id) || 0,
      }))
      .sort((a, b) => a.quantity - b.quantity || String(a.name).localeCompare(String(b.name)))
      .slice(0, limit),
  };
}

/**
 * Revenue by category.
 *
 * The order line stores only the menu item, so the category is looked up
 * against the menu the app already has loaded. A line whose item has since
 * been deleted has nowhere to go and lands in "No category" — which is honest,
 * and adds up.
 */
export function categoryTotals(orders, products, categories) {
  const productCategory = new Map();
  products.forEach((product) => {
    if (product && product._id) productCategory.set(product._id, idOf(product.category));
  });

  const names = new Map();
  categories.forEach((category) => {
    if (category && category._id) names.set(category._id, category.name);
  });

  const totals = new Map();

  itemTotals(orders).forEach((entry) => {
    const categoryId = productCategory.get(entry.menuItemId) || '';
    const key = categoryId && names.has(categoryId) ? categoryId : '';
    const row = totals.get(key) || {
      _id: key,
      name: key ? names.get(key) : 'No category',
      revenue: 0,
      quantity: 0,
    };
    row.revenue = round2(row.revenue + entry.revenue);
    row.quantity += entry.quantity;
    totals.set(key, row);
  });

  return Array.from(totals.values()).sort((a, b) => b.revenue - a.revenue);
}

/* ── how the money arrived ──────────────────────────────────────────────── */

/**
 * Three buckets, not two, and they add up to the period's revenue.
 *
 * "Cash vs invoice" alone overstates the drawer: an order can be marked cash
 * and still be sitting unpaid on a table. So the split is what is actually
 * collected, what is still open, and what went onto a customer's balance.
 */
export function paymentSplit(orders) {
  let collected = 0;
  let collectedCount = 0;
  let open = 0;
  let openCount = 0;
  let account = 0;
  let accountCount = 0;

  orders.forEach((order) => {
    const total = num(order.total);
    if (isInvoice(order)) {
      account += total;
      accountCount += 1;
    } else if (isOpen(order)) {
      open += total;
      openCount += 1;
    } else {
      collected += total;
      collectedCount += 1;
    }
  });

  return [
    { key: 'collected', label: 'Collected', value: round2(collected), count: collectedCount },
    { key: 'open', label: 'Not collected yet', value: round2(open), count: openCount },
    { key: 'account', label: 'On account', value: round2(account), count: accountCount },
  ];
}

export function orderTypeTotals(orders) {
  const order = ['dine-in', 'takeaway', 'delivery'];
  const labels = { 'dine-in': 'Dine-in', takeaway: 'Takeaway', delivery: 'Delivery' };
  const totals = new Map();

  orders.forEach((entry) => {
    const key = String(entry.orderType || '').toLowerCase() || 'dine-in';
    const row = totals.get(key) || { key, label: labels[key] || key, value: 0, count: 0 };
    row.value = round2(row.value + num(entry.total));
    row.count += 1;
    totals.set(key, row);
  });

  // Fixed order, so a colour always means the same thing — a chart that
  // repaints when takeaway overtakes delivery teaches the reader nothing.
  return order
    .filter((key) => totals.has(key))
    .map((key) => totals.get(key))
    .concat(Array.from(totals.values()).filter((row) => !order.includes(row.key)));
}

/* ── who rang it in ─────────────────────────────────────────────────────── */

export function staffTotals(orders) {
  const staff = new Map();

  orders.forEach((order) => {
    const user = order.user;
    const id = idOf(user) || 'unknown';
    const row = staff.get(id) || {
      _id: id,
      name: (user && user.name) || 'Unknown',
      username: (user && user.username) || '',
      revenue: 0,
      orders: 0,
    };
    row.revenue = round2(row.revenue + num(order.total));
    row.orders += 1;
    staff.set(id, row);
  });

  return Array.from(staff.values())
    .map((row) => ({ ...row, average: row.orders ? round2(row.revenue / row.orders) : 0 }))
    .sort((a, b) => b.revenue - a.revenue);
}

/* ── the feed ───────────────────────────────────────────────────────────── */

export function latestOrders(orders, limit = 7) {
  return orders
    .slice()
    .sort((a, b) => orderMoment(b).valueOf() - orderMoment(a).valueOf())
    .slice(0, limit);
}

/**
 * Tickets that have been open a while.
 *
 * Timed from `created_at`, so this is the only ageing figure on the screen
 * that is not affected by the clock problem in `date`.
 */
export function stalledOrders(orders, minutes = 45, now = moment()) {
  return orders.filter((order) => isOpen(order) && now.diff(orderMoment(order), 'minutes') >= minutes);
}
